/* ------------------------------------------------------------------
 * Brief 66 - product card modal. No dependencies, deferred, one delegated
 * listener on document.
 *
 * Modal content is fetched on open from
 *   /products/<handle>?section_id=phj-product-modal
 * and cached by handle in a Map. Prefetch on pointerenter / focusin / touchstart.
 * On any non-200 or network error the card's own href is followed silently -
 * which is also the no-JS path, so nothing extra is needed for that.
 *
 * Cart: the theme's own WAU.AjaxCart.addToCart(form, context, config, false, false)
 * is called, so the cart count and drawer refresh exactly as they do from a product
 * page. Config comes from the #cart-config JSON script the layout already renders.
 * ------------------------------------------------------------------ */
(function () {
  'use strict';

  var CACHE = new Map();
  var INFLIGHT = new Map();
  var root = null;      /* the live modal element */
  var opener = null;    /* element focus returns to */
  var lastFocus = [];

  function sectionUrl(handle) {
    return '/products/' + handle + '?section_id=phj-product-modal';
  }

  function fetchModal(handle) {
    if (CACHE.has(handle)) return Promise.resolve(CACHE.get(handle));
    if (INFLIGHT.has(handle)) return INFLIGHT.get(handle);
    var p = fetch(sectionUrl(handle), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('status ' + r.status);
        return r.text();
      })
      .then(function (html) { CACHE.set(handle, html); INFLIGHT.delete(handle); return html; })
      .catch(function (e) { INFLIGHT.delete(handle); throw e; });
    INFLIGHT.set(handle, p);
    return p;
  }

  /* ---------- focus trap ---------- */
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

  function trap(e) {
    if (!root || e.key !== 'Tab') return;
    var items = Array.prototype.slice.call(root.querySelectorAll(FOCUSABLE))
      .filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function onKey(e) {
    if (!root) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    trap(e);
  }

  /* ---------- open / close ---------- */
  function lockScroll(on) {
    if (on) {
      lastFocus = [window.pageXOffset, window.pageYOffset];
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }

  function mount(html) {
    var host = document.createElement('div');
    host.innerHTML = html;
    var el = host.querySelector('[data-phj-modal]');
    if (!el) return null;
    document.body.appendChild(el);
    return el;
  }

  function open(handle, fromEl, href) {
    opener = fromEl;
    fetchModal(handle).then(function (html) {
      if (root) close(true);
      root = mount(html);
      if (!root) { if (href) window.location.href = href; return; }
      root.hidden = false;
      lockScroll(true);
      document.addEventListener('keydown', onKey, true);
      var focusTarget = root.querySelector('[data-phj-close]') || root.querySelector(FOCUSABLE);
      if (focusTarget) focusTarget.focus();
      wireGallery(root);
    }).catch(function () {
      /* silent fallback: go where the card already points */
      if (href) window.location.href = href;
    });
  }

  function close(skipFocus) {
    if (!root) return;
    document.removeEventListener('keydown', onKey, true);
    if (root.parentNode) root.parentNode.removeChild(root);
    root = null;
    lockScroll(false);
    if (!skipFocus && opener && document.contains(opener)) opener.focus();
    opener = null;
  }

  /* ---------- gallery ---------- */
  function wireGallery(scope) {
    var stage = scope.querySelector('[data-phj-stage]');
    if (!stage) return;
    var slides = Array.prototype.slice.call(stage.querySelectorAll('.phj-gal__slide'));
    var thumbs = Array.prototype.slice.call(scope.querySelectorAll('[data-phj-thumb]'));
    function goTo(i) {
      if (!slides[i]) return;
      stage.scrollTo({ left: slides[i].offsetLeft - stage.offsetLeft, behavior: 'smooth' });
      thumbs.forEach(function (t, n) { t.setAttribute('aria-current', n === i ? 'true' : 'false'); });
    }
    scope.addEventListener('click', function (e) {
      var t = e.target.closest('[data-phj-thumb]');
      if (t) { goTo(parseInt(t.getAttribute('data-phj-thumb'), 10)); return; }
      var nav = e.target.closest('[data-phj-nav]');
      if (nav) {
        var cur = thumbs.findIndex(function (x) { return x.getAttribute('aria-current') === 'true'; });
        if (cur < 0) cur = 0;
        goTo(nav.getAttribute('data-phj-nav') === 'next'
          ? Math.min(cur + 1, slides.length - 1) : Math.max(cur - 1, 0));
      }
    });
  }

  /* ---------- cart ---------- */
  function addToCart(form) {
    var cfgEl = document.getElementById('cart-config');
    var cfg = {};
    try { cfg = JSON.parse((cfgEl && cfgEl.innerHTML) || '{}'); } catch (e) { cfg = {}; }
    var context = form.closest('[data-phj-form-context]') || form;
    if (window.WAU && window.WAU.AjaxCart && typeof window.WAU.AjaxCart.addToCart === 'function') {
      window.WAU.AjaxCart.addToCart(form, context, cfg, false, false);
      return true;
    }
    /* the theme's own helper, if AjaxCart is not initialised on this page */
    if (window.WAU && window.WAU.ThemeCart && typeof window.WAU.ThemeCart.addItemFromForm === 'function') {
      window.WAU.ThemeCart.addItemFromForm(form).then(function () {
        if (window.WAU.ThemeCart.getCart) window.WAU.ThemeCart.getCart();
      });
      return true;
    }
    return false;   /* let the form post normally */
  }

  /* ---------- one delegated listener ---------- */
  document.addEventListener('click', function (e) {
    /* modifier and middle clicks belong to the browser, untouched */
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var closeEl = e.target.closest('[data-phj-close]');
    if (closeEl && root) { e.preventDefault(); close(); return; }
    if (root && e.target.closest('[data-phj-backdrop]')) { e.preventDefault(); close(); return; }

    var trigger = e.target.closest('[data-phj-open]');
    if (trigger) {
      var handle = trigger.getAttribute('data-phj-open');
      if (!handle) return;
      var href = trigger.getAttribute('href') || (trigger.closest('.phj-card') || {}).dataset
        ? (trigger.closest('.phj-card') || document.createElement('div')).getAttribute('data-phj-url')
        : null;
      e.preventDefault();
      open(handle, trigger, href || trigger.getAttribute('href'));
    }
  });

  /* prefetch */
  function pre(e) {
    var t = e.target.closest && e.target.closest('[data-phj-open]');
    if (!t) return;
    var h = t.getAttribute('data-phj-open');
    if (h && !CACHE.has(h)) fetchModal(h).catch(function () {});
  }
  document.addEventListener('pointerenter', pre, true);
  document.addEventListener('focusin', pre);
  document.addEventListener('touchstart', pre, { passive: true });

  /* Add to cart inside the modal, and only inside it. The attribute is ours and appears
     nowhere else in the theme, but from the kokuto page on there is a real /cart/add form in
     the same document - the page's own product form, plus the sticky bar. Requiring an
     ancestor .phj-modal makes that a structural guarantee rather than a naming convention. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-phj-add-form]');
    if (!form || !form.closest('.phj-modal')) return;
    if (addToCart(form)) e.preventDefault();
  });
})();
